import * as am4core from '@amcharts/amcharts4/core';

import { formatDollarsCompact, locale } from '@trqk/utils';

/* eslint-disable no-param-reassign */

/**
 * Disables amCharts scrollbar grips and removes the scrollbar tooltip.
 */
export const unstyleScrollbar = (scrollbar: am4core.Scrollbar): void => {
    scrollbar.startGrip.disabled = true;
    scrollbar.endGrip.disabled = true;
    scrollbar.showSystemTooltip = false;
};

/* eslint-enable no-param-reassign */

/**
 * Returns a message to show to the user about how values smaller than `minValue` are not shown.
 * @param {number} minValue a minimum value
 */
export const getSmallValuesMessage = (minValue: number): string =>
    `${
        locale.enUS.STATEMENTS.INFO.SMALL_VALUES_NOT_SHOWN.PART_1
    } ${formatDollarsCompact(minValue)} ${
        locale.enUS.STATEMENTS.INFO.SMALL_VALUES_NOT_SHOWN.PART_2
    }`;

/**
 * A sorting function for use with Array.prototype.sort() on an array of ProSource objects.
 * Sorts the list in descending order by max_earnings_per_play.
 * @param {ProSource} sourceA
 * @param {ProSource} sourceB
 */
export const sortProSourceByMaxEarningsPerPlay = (
    sourceA: ProSource,
    sourceB: ProSource,
): number => {
    if (
        sourceA.max_earnings_per_play === null ||
        sourceA.max_earnings_per_play === undefined ||
        sourceB.max_earnings_per_play === null ||
        sourceB.max_earnings_per_play === undefined
    ) {
        return 0;
    }

    if (sourceB.max_earnings_per_play > sourceA.max_earnings_per_play) {
        return 1;
    }

    if (sourceA.max_earnings_per_play > sourceB.max_earnings_per_play) {
        return -1;
    }

    return 0;
};

/**
 * A sorting function for use with Array.prototype.sort() on an array of ProSource objects.
 * Sorts the list in descending order by max_earnings_per_second.
 * @param {ProSource} sourceA
 * @param {ProSource} sourceB
 */
export const sortProSourceByMaxEarningsPerSecond = (
    sourceA: ProSource,
    sourceB: ProSource,
): number => {
    if (
        sourceA.max_earnings_per_second === null ||
        sourceA.max_earnings_per_second === undefined ||
        sourceB.max_earnings_per_second === null ||
        sourceB.max_earnings_per_second === undefined
    ) {
        return 0;
    }

    if (sourceB.max_earnings_per_second > sourceA.max_earnings_per_second) {
        return 1;
    }

    if (sourceA.max_earnings_per_second > sourceB.max_earnings_per_second) {
        return -1;
    }

    return 0;
};

/**
 * A filter predicate for using with Array.prototype.filter() on an array of ProSource objects.
 * Returns true if source.max_earnings_per_play is greater than or equal to the minimum value.
 * @param {ProSource} source an element in an array of ProSource objects
 * @param {number} minValue
 */
export const filterSmallValuesByMaxEarningsPerPlay = (
    source: Maybe<ProSource> | undefined,
    minValue: number,
): boolean =>
    source?.max_earnings_per_play !== null &&
    source?.max_earnings_per_play !== undefined &&
    source.max_earnings_per_play >= minValue;

/**
 * A filter predicate for using with Array.prototype.filter() on an array of ProSource objects.
 * Returns true if both source.max_earnings_per_second is greater than or equal to the minimum
 * value.
 * @param source {ProSource} an element in an array of ProSource objects
 * @param minValue {number}
 */
export const filterSmallValuesByMaxEarningsPerSecond = (
    source: Maybe<ProSource> | undefined,
    minValue: number,
): boolean =>
    source?.max_earnings_per_second !== null &&
    source?.max_earnings_per_second !== undefined &&
    source.max_earnings_per_second >= minValue;
